import { useEffect, useState } from "react";
import { parseISO } from "date-fns";
import { CheckCircleIcon, PencilIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { Button, Field, Input, Label, Switch } from "@headlessui/react";
import { useAppSelector } from "../../../app/hooks";
import { UserRole } from "../../../app/userSlice";
import ModalForm from "../../../components/ModalForm";
import AlertMessage, { AlertLevel } from "../../../components/AlertMessage";
import RequiredField from "../../../components/RequiredField";
import {
	SchoolActivationViewModel,
	SchoolSpaViewModel,
	useActivateSchoolMutation,
	useAdjustExpirationDateMutation,
	useDeactivateSchoolMutation,
	useGetExpirationDateQuery,
} from "../schoolsApiSlice";

interface ActivationSettingsProps {
	schoolInfo: SchoolSpaViewModel;
	setSchoolInfo: (schoolInfo: SchoolSpaViewModel) => void;
	setErrorMessage: (message: string) => void;
}

export default function ActivationSettings({
	schoolInfo,
	setSchoolInfo,
	setErrorMessage,
}: ActivationSettingsProps) {
	const { role } = useAppSelector((state) => state.user);

	const [activationModel, setActivationModel] = useState<SchoolActivationViewModel>({
		active: schoolInfo.active,
		expirationModel: {
			schoolId: schoolInfo.id,
			schoolExpirationDate: schoolInfo.expirationDate,
			noExpirationDate: schoolInfo.expirationDateString === "Never",
		},
	});
	const [isFormOpen, setIsFormOpen] = useState(false);
	const [disableConfirm, setDisableConfirm] = useState(false);

	const [activate] = useActivateSchoolMutation();
	const [deactivate] = useDeactivateSchoolMutation();
	const [adjustExpirationDate] = useAdjustExpirationDateMutation();

	async function handleSave() {
		let errorMessages = "";
		let expirationDateUpdated = false;
		let activationUpdated = false;

		// Update expiration date
		await adjustExpirationDate(activationModel.expirationModel)
			.unwrap()
			.then(() => {
				expirationDateUpdated = true;
			})
			.catch((error: any) => {
				const errorMsg = error?.data || "An error occurred adjusting expiration date.";
				errorMessages += `- ${errorMsg}\n`;
			});

		// Update activation status
		const updateActivation = activationModel.active
			? activate(schoolInfo.id)
			: deactivate(schoolInfo.id);
		await updateActivation
			.unwrap()
			.then(() => {
				activationUpdated = true;
			})
			.catch((error: any) => {
				const errorMsg =
					error?.data ||
					`An error occurred with ${activationModel.active ? "activation" : "deactivation"}.`;
				errorMessages += `- ${errorMsg}\n`;
			});

		// Update schoolInfo only if either of the calls were successful
		if (expirationDateUpdated || activationUpdated) {
			const updatedSchoolInfo = { ...schoolInfo };

			if (expirationDateUpdated) {
				updatedSchoolInfo.expirationDate =
					activationModel.expirationModel.schoolExpirationDate;
				updatedSchoolInfo.expirationDateString = activationModel.expirationModel
					.noExpirationDate
					? "Never"
					: new Date(
							activationModel.expirationModel.schoolExpirationDate
						).toLocaleDateString();
				updatedSchoolInfo.hasExpired = false;
			}

			if (activationUpdated) {
				updatedSchoolInfo.active = activationModel.active;
			}

			setSchoolInfo(updatedSchoolInfo);
		}

		setIsFormOpen(false);
		setErrorMessage(errorMessages);
	}

	return (
		<div className="flex items-center">
			{schoolInfo.active ? (
				<CheckCircleIcon className="size-18 text-green-500" />
			) : (
				<XCircleIcon className="size-18 text-red-500" />
			)}
			<div className="ml-4">
				<p
					className={`font-semibold ${schoolInfo.active ? "text-green-700" : "text-red-700"}`}>
					{schoolInfo.active ? "Active" : "Inactive"}
				</p>
				<div className="flex items-center">
					<p>
						{schoolInfo.hasExpired ? "Expired" : "Expires"}:{" "}
						<span className="font-semibold">{schoolInfo.expirationDateString}</span>
					</p>
					{role === UserRole.Admin && (
						<div className="ml-3">
							<Button
								className="border-2 p-1 rounded-md shadow-sm border-gray-900/20 cursor-pointer"
								onClick={() => setIsFormOpen(true)}>
								<PencilIcon className="size-5 " />
							</Button>

							{isFormOpen && (
								<ModalForm
									open={isFormOpen}
									title="Activation Settings"
									form={
										<ActivationForm
											schoolId={schoolInfo.id}
											active={schoolInfo.active}
											updateActivationModel={setActivationModel}
											disableConfirm={setDisableConfirm}
										/>
									}
									confirmLabel="Save"
									disableConfirm={disableConfirm}
									onConfirm={handleSave}
									onCancel={() => setIsFormOpen(false)}
								/>
							)}
						</div>
					)}
				</div>
			</div>
		</div>
	);
}

interface ActivationFormProps {
	schoolId: number;
	active: boolean;
	updateActivationModel: (model: SchoolActivationViewModel) => void;
	disableConfirm: (disable: boolean) => void;
}

function ActivationForm(props: ActivationFormProps) {
	const dateNow = new Date().toISOString().slice(0, 10);

	const [active, setActive] = useState(props.active);
	const [expirationDate, setExpirationDate] = useState("");
	const [noExpirationDate, setNoExpirationDate] = useState(true);
	const [isDateAllowed, setIsDateAllowed] = useState(true);
	const [errorMessage, setErrorMessage] = useState("");

	const expirationResult = useGetExpirationDateQuery(props.schoolId, {
		refetchOnMountOrArgChange: true,
	});
	const expirationInfo = expirationResult.data;

	useEffect(() => {
		if (!expirationInfo) return;
		setExpirationDate(new Date(expirationInfo.schoolExpirationDate).toISOString().slice(0, 10));
		setNoExpirationDate(expirationInfo.noExpirationDate);
	}, [expirationInfo]);

	useEffect(() => {
		// Only update the activation model if we have a valid date or no expiration date is set
		const date = expirationDate ? parseISO(expirationDate) : null;
		const isValidDate = date !== null && !isNaN(date.getTime());

		if (isValidDate || noExpirationDate) {
			props.updateActivationModel({
				active: active,
				expirationModel: {
					schoolId: props.schoolId,
					schoolExpirationDate: date!,
					noExpirationDate: noExpirationDate,
				},
			});
		}

		setIsDateAllowed(noExpirationDate || !active || (isValidDate && expirationDate > dateNow));
	}, [expirationDate, noExpirationDate, active]);

	useEffect(() => {
		if (isDateAllowed) {
			setErrorMessage("");
			props.disableConfirm(false);
		} else {
			setErrorMessage("The expiration date must be in the future.");
			props.disableConfirm(true);
		}
	}, [isDateAllowed]);

	return (
		<div className="mt-3">
			<AlertMessage
				className="mb-2 text-sm"
				type={AlertLevel.Warning}
				message={errorMessage}
				showIcon={true}
			/>
			<div className="grid grid-cols-2 gap-4">
				<Field>
					<Label htmlFor="activeSwitch" className="font-semibold text-sm">
						Activated?
					</Label>
					<div className="mt-2">
						<div className="flex items-center ml-2">
							<Label className="sr-only">Activated Toggle</Label>
							<Switch
								checked={active}
								name="activeSwitch"
								onChange={setActive}
								className="group relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 data-[checked]:bg-primary-600">
								<span
									aria-hidden="true"
									className="pointer-events-none inline-block size-6 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5"
								/>
							</Switch>

							<div className="ml-3 flex gap-2 items-center">
								{active ? (
									<CheckCircleIcon className="size-7 text-green-500" />
								) : (
									<XCircleIcon className="size-7 text-red-500" />
								)}
								<p
									className={`font-semibold ${active ? "text-green-700" : "text-red-700"}`}>
									{active ? "Active" : "Inactive"}
								</p>
							</div>
						</div>
					</div>
				</Field>
				<Field>
					<Label htmlFor="expirationDate" className="font-semibold text-sm">
						Expiration Date
						<RequiredField />
					</Label>
					<div className="flex gap-3 mt-2 items-center">
						<Input
							id="noExpirationCheckbox"
							type="checkbox"
							checked={noExpirationDate}
							onChange={(e) => setNoExpirationDate(e.target.checked)}
							className="size-4 rounded-sm border-gray-300 text-primary-600 focus:ring-primary-600"
						/>
						<p className="text-sm">No Expiration Date</p>
					</div>
					{!noExpirationDate && (
						<Input
							type="date"
							name="expirationDate"
							id="expirationDate"
							required
							readOnly={noExpirationDate}
							className={`${!isDateAllowed ? "focus:ring-red-600 ring-red-500" : "focus:ring-primary-600 ring-gray-300"} block w-full mt-2 rounded-lg border-0 text-sm ring-1 ring-inset focus:ring-2 focus:ring-inset`}
							value={expirationDate}
							min={dateNow}
							onChange={(e) => setExpirationDate(e.target.value)}
						/>
					)}
				</Field>
			</div>
		</div>
	);
}
